import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from '../Utility'
import getText from '../../shared/i18n/labels'

// MODAL
import { Modal } from 'react-bootstrap'

// BUTTONS
import { BtnEmpty } from '../BtnEmpty'

// COMPONENTS
import { UploadFileLabel } from '../form/UploadFileLabel'

/**
 * ImportModal  un componente React che fornisce un'interfaccia di modalit  per caricare file.
 *
 * @param {Array<File>} dataFile - File iniziali da caricare nel componente.
 * @param {Object} errorDataFile - Stato di errore iniziale per la validazione del file.
 * @param {Array<string>} allowedFileExtensions - Elenco delle estensioni di file consentite per l'upload.
 * @param {number} maximumFileSize - La dimensione massima consentita per l'upload dei file in byte.
 * @param {Function} onHide - Funzione di callback attivata quando la modale viene chiusa.
 * @param {Function} onSend - Funzione di callback attivata quando i file vengono validati con successo e inviati.
 * @param {Function} onUpload - Funzione di callback quando il file viene caricato.
 * @param {string} title - Titolo della modale.
 * @param {Function} buttonClose - Componente personalizzato per il pulsante di chiusura.
 * @param {Function} buttonLoad - Componente personalizzato per il pulsante di caricamento del file.
 * @param {Function} secondaryTitle - Componente personalizzato per un titolo secondario opzionale.
 * @param {Function} buttonUpload - Componente personalizzato per il pulsante di caricamento.
 * @param {Function} extraButtons - Componente personalizzato per pulsanti aggiuntivi.
 * @param {boolean} show - Flag booleano per controllare la visibilità della modale.
 *
 */
export function ImportModal(props) {
  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [dataFile, setDataFile] = useState(props.dataFile || [])
  const [errorDataFile, setErrorDataFile] = useState(
    props.errorDataFile || { isValid: true, msg: '' }
  )

  useEffect(() => {
    if (props.dataFile && props.dataFile !== dataFile) {
      setDataFile(props.dataFile)
    }
    if (props.errorDataFile && props.errorDataFile !== errorDataFile) {
      setErrorDataFile(props.errorDataFile)
    }
  })

  /**
   * Funzione per gestire l'evento di caricamento del file.
   * Controlla se il file caricato è valido e lo aggiunge all'array
   * dataFile se è corretto.
   *
   * @param {Event} val - L'evento di caricamento del file.
   */
  const handleUploadFile = (val) => {
    if (isEmpty(val)) {
      setErrorDataFile({ isValid: false, msg: labels.REQUIRED_FILE })
    }
    let files = []
    for (let file of val.target.files) {
      if (!hasCorrectFileType(file.name)) {
        setErrorDataFile({
          isValid: false,
          msg: `${labels.INVALID_FILE_EXTENSION} ${allowedFileExtensions.join(', ')}`
        })
      } else if (!checkFileDimension(val.target.files)) {
        setErrorDataFile({ isValid: false, msg: labels.FILE_TOO_LARGE })
      } else {
        setErrorDataFile({ isValid: true, msg: '' })
        files.push(file)
      }
    }

    if (files.length > 0) setDataFile(files)
  }

  /**
   * Verifica se la dimensione complessiva dei file è inferiore
   * alla dimensione massima consentita.
   *
   * @param {FileList} files - L'elenco dei file da verificare.
   *
   * @returns {boolean} True se la dimensione complessiva dei file è
   *                    inferiore alla dimensione massima consentita,
   *                    false altrimenti.
   */
  const checkFileDimension = (files) => {
    let sumDimensionFiles = 0

    for (let file of files) {
      sumDimensionFiles += file.size
    }
    return sumDimensionFiles < maximumFileSize
  }

  /**
   * Verifica se il nome del file ha un'estensione corretta.
   *
   * @param {string} name - Il nome del file da verificare.
   *
   * @returns {boolean} Restituisce true se l'estensione del file è tra quelle consentite,
   *                    o false se non ci sono estensioni consentite specificate.
   */
  const hasCorrectFileType = (name) => {
    if (allowedFileExtensions?.length === 0) return true
    return allowedFileExtensions.indexOf(name.split('.').pop().toLowerCase()) !== -1
  }

  /**
   * Popola una stringa che indica i tipi di file accettati, secondo le specifiche del tag input
   *
   * @param {string[]} [allowedFileExtensions=[]] - Un array di estensioni di file consentite.
   * @returns {string} Una stringa separata da virgole di estensioni di file precedute da un punto.
   */
  const fillAcceptAttribute = (allowedFileExtensions = []) => {
    let accept = ''
    allowedFileExtensions.forEach((ext) => (accept += `.${ext},`))
    return accept
  }

  const allowedFileExtensions = props.allowedFileExtensions || []
  const maximumFileSize = 20e6
  const onHide = props.onHide || undefined
  const onSend = props.onSend || undefined
  const onUpload = props.onUpload || handleUploadFile
  const title = props.title || labels.IMPORT_FILE

  const ButtonClose =
    props.buttonClose ||
    (() => (
      <BtnEmpty text={labels.CANCEL} classCustom="float-start" handlerOnClick={() => onHide()} />
    ))

  const ButtonLoadFiles =
    props.buttonLoad || (() => <UploadFileLabel forLabel={'dataFile'} label={'CARICA FILE'} />)

  const SecondaryTitle = props.secondaryTitle || (() => <></>)

  const ButtonUpload =
    props.buttonUpload ||
    (() => (
      <button
        disabled={!errorDataFile.isValid || !dataFile}
        onClick={() => handleFormSubmit()}
        type="button"
        className="btn btn-primary">
        {labels.CONFIRM}
      </button>
    ))
  const ExtraButtons = props.extraButtons || (() => '')

  /**
   * Funzione per gestire l'invio del modulo.
   * Verifica se ci sono file caricati e se la loro dimensione complessiva
   * è inferiore alla dimensione massima consentita. Se si, esegue la funzione
   * di invio specificata in {@link onSend}.
   *
   */
  const handleFormSubmit = () => {
    if (!dataFile?.length > 0) {
      setErrorDataFile({ isValid: false, msg: labels.FILE_REQUIRED })
    } else {
      setErrorDataFile({ isValid: true, msg: '' })
      onSend(dataFile)
    }
  }

  return (
    <>
      <Modal
        backdrop="static"
        show={props.show}
        onHide={onHide}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered>
        <div className="bar"></div>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            <h2>{title}</h2>
            <SecondaryTitle />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="image-upload text-start mt-3">
            <ButtonLoadFiles />
            <input
              id={`dataFile`}
              type="file"
              name={`dataFile`}
              multiple
              accept={fillAcceptAttribute(allowedFileExtensions)}
              onChange={(e) => onUpload(e)}
            />
            <div className="certificatiLoaded">
              {!errorDataFile.isValid ? (
                <div className="certificatiLoaded-error">{errorDataFile.msg}</div>
              ) : (
                <div>
                  {dataFile.length > 0 &&
                    dataFile.map((file, index) => {
                      return (
                        <div key={'file-' + index} className="green-label text-truncate">
                          <i className="star-icon-check star-icon me-2" />
                          {file.name}
                        </div>
                      )
                    })}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between align-content-end mt-0">
            <ButtonClose />
            <ExtraButtons />
            <ButtonUpload />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
